import './ContactDetail.scss';
import React, { useCallback, useState } from 'react';
import {
  Envelope,
  Phone,
  LinkedinLogo,
  TwitterLogo,
  GithubLogo,
  PencilSimple,
  Trash,
  ArrowRight,
  BellRinging,
  CalendarCheck,
  Warning,
  HandWaving,
  Check,
  X,
} from '@phosphor-icons/react';
import {
  siGmail,
  siGooglecalendar,
  siGoogle,
  siSlack,
  siWhatsapp,
  siGithub,
  siX,
  siImessage,
} from 'simple-icons';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { ContactEntity, ContactSignal, TIER_LABELS, TIER_DESCRIPTIONS, TIER_CADENCE_DAYS } from '../../types/entity';
import { formatRelativeTime } from '../../utils/time';

interface ContactDetailProps {
  windowId: string;
  spaceId: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SignalIcon({ type }: { type: ContactSignal['type'] }) {
  switch (type) {
    case 'job_change':      return <BellRinging size={14} />;
    case 'calendar_meeting':return <CalendarCheck size={14} />;
    case 'email_activity':  return <Envelope size={14} />;
    case 'linkedin_post':   return <LinkedinLogo size={14} />;
    default:                return <Warning size={14} />;
  }
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function getAvatarColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return hash % 6;
}

function isStale(contact: ContactEntity): boolean {
  const cadence = TIER_CADENCE_DAYS[contact.skeleton.tier];
  if (!cadence || !contact.skeleton.lastInteraction?.at) return false;
  return (Date.now() - new Date(contact.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
}

// ── Source badges ──────────────────────────────────────────────────────────

function SiIcon({ icon, size = 11 }: { icon: { path: string }; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff" aria-hidden>
      <path d={icon.path} />
    </svg>
  );
}

interface SourceMeta {
  label: string;
  gradient: string;
  icon: React.ReactNode;
}

const SOURCE_META: Record<string, SourceMeta> = {
  gmail: {
    label: 'Gmail',
    gradient: 'linear-gradient(160deg, #ff6d57 0%, #EA4335 100%)',
    icon: <SiIcon icon={siGmail} />,
  },
  calendar: {
    label: 'Calendar',
    gradient: 'linear-gradient(160deg, #5fa8fb 0%, #1a73e8 100%)',
    icon: <SiIcon icon={siGooglecalendar} />,
  },
  google_contacts: {
    label: 'Contacts',
    gradient: 'linear-gradient(160deg, #5bc87a 0%, #34A853 100%)',
    icon: <SiIcon icon={siGoogle} />,
  },
  linkedin: {
    label: 'LinkedIn',
    gradient: 'linear-gradient(160deg, #2d87e0 0%, #0A66C2 100%)',
    icon: <LinkedinLogo size={11} color="#fff" weight="fill" />,
  },
  slack: {
    label: 'Slack',
    gradient: 'linear-gradient(160deg, #ed3278 0%, #611f69 100%)',
    icon: <SiIcon icon={siSlack} />,
  },
  whatsapp: {
    label: 'WhatsApp',
    gradient: 'linear-gradient(160deg, #40e07a 0%, #128C7E 100%)',
    icon: <SiIcon icon={siWhatsapp} />,
  },
  github: {
    label: 'GitHub',
    gradient: 'linear-gradient(160deg, #5a5a5a 0%, #24292E 100%)',
    icon: <SiIcon icon={siGithub} />,
  },
  twitter: {
    label: 'X / Twitter',
    gradient: 'linear-gradient(160deg, #333 0%, #000 100%)',
    icon: <SiIcon icon={siX} />,
  },
  imessage: {
    label: 'iMessage',
    gradient: 'linear-gradient(160deg, #60e87a 0%, #34C759 100%)',
    icon: <SiIcon icon={siImessage} />,
  },
  manual: {
    label: 'Manual',
    gradient: 'linear-gradient(160deg, #a0a0a0 0%, #666 100%)',
    icon: <HandWaving size={11} color="#fff" weight="fill" />,
  },
};

function SourceBadge({ source }: { source: string }) {
  const meta: SourceMeta = SOURCE_META[source] ?? {
    label: source.charAt(0).toUpperCase() + source.slice(1),
    gradient: 'linear-gradient(160deg, #a0a0a0 0%, #666 100%)',
    icon: <HandWaving size={11} color="#fff" weight="fill" />,
  };
  return (
    <div className="contact-detail__source-badge">
      <span className="contact-detail__source-icon" style={{ background: meta.gradient }}>
        {meta.icon}
      </span>
      <span className="contact-detail__source-label">{meta.label}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export const ContactDetail: React.FC<ContactDetailProps> = ({ windowId, spaceId }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const selectedContactId = store((s) => s.selectedContactId);
  const contacts = store((s) => s.contacts);
  const deleteContact = store((s) => s.deleteContact);
  const openForm = store((s) => s.openForm);

  const contact = contacts.find((c) => c._id === selectedContactId);

  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleEdit = useCallback(() => {
    if (contact) openForm(contact._id);
  }, [contact, openForm]);

  const handleDeleteClick = useCallback(() => {
    setConfirmingDelete(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!contact) return;
    setConfirmingDelete(false);
    await deleteContact(contact._id);
  }, [contact, deleteContact]);

  const handleDeleteCancel = useCallback(() => {
    setConfirmingDelete(false);
  }, []);

  if (!contact) return null;

  const sk = contact.skeleton;
  const stale = isStale(contact);

  return (
    <div className="contact-detail">
      <div className="contact-detail__constrained">

        {/* Header */}
        <div className="contact-detail__header">
          <div
            className="contact-detail__avatar"
            data-color={sk.avatarUrl ? undefined : getAvatarColorIndex(contact.name)}
          >
            {sk.avatarUrl ? (
              <img src={sk.avatarUrl} alt={contact.name} className="contact-detail__avatar-img" />
            ) : (
              <span className="contact-detail__avatar-initials">{getInitials(contact.name)}</span>
            )}
          </div>
          <div className="contact-detail__header-info">
            <h2 className="contact-detail__name">{contact.name}</h2>
            {(sk.title || sk.company) && (
              <p className="contact-detail__role">
                {sk.title}{sk.title && sk.company ? ' · ' : ''}{sk.company}
              </p>
            )}
            <span className={`contact-detail__tier-badge contact-detail__tier-badge--${sk.tier}`}>
              {TIER_LABELS[sk.tier]}
              <span className="contact-detail__tier-desc"> — {TIER_DESCRIPTIONS[sk.tier]}</span>
            </span>
          </div>
          <div className="contact-detail__header-actions">
            {confirmingDelete ? (
              <>
                <span className="contact-detail__delete-confirm-label">Delete?</span>
                <button
                  className="contact-detail__icon-btn contact-detail__icon-btn--danger-solid"
                  onClick={handleDeleteConfirm}
                  title="Confirm delete"
                >
                  <Check size={15} />
                </button>
                <button
                  className="contact-detail__icon-btn"
                  onClick={handleDeleteCancel}
                  title="Cancel"
                >
                  <X size={15} />
                </button>
              </>
            ) : (
              <>
                <button className="contact-detail__icon-btn" onClick={handleEdit} title="Edit">
                  <PencilSimple size={15} />
                </button>
                <button
                  className="contact-detail__icon-btn contact-detail__icon-btn--danger"
                  onClick={handleDeleteClick}
                  title="Delete"
                >
                  <Trash size={15} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stale alert */}
        {stale && (
          <div className="contact-detail__stale-alert">
            <Warning size={14} />
            Overdue for engagement based on {TIER_LABELS[sk.tier]} cadence
          </div>
        )}

        {/* ── Contact info + socials ── */}
        <div className="contact-detail__section contact-detail__section--first">
          <h3 className="contact-detail__section-title">Contact</h3>
          <div className="contact-detail__info-rows">

            {sk.email ? (
              <a href={`mailto:${sk.email}`} className="contact-detail__info-row">
                <Envelope size={14} />
                <span>{sk.email}</span>
                {sk.preferredChannel === 'email' && <span className="contact-detail__preferred-badge">preferred</span>}
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <Envelope size={14} />
                <span>Add email</span>
              </button>
            )}

            {sk.phone && (
              <a href={`tel:${sk.phone}`} className="contact-detail__info-row">
                <Phone size={14} />
                <span>{sk.phone}</span>
                {sk.preferredChannel === 'phone' && <span className="contact-detail__preferred-badge">preferred</span>}
              </a>
            )}

            {sk.linkedinUrl && (
              <a href={sk.linkedinUrl} target="_blank" rel="noreferrer" className="contact-detail__info-row">
                <LinkedinLogo size={14} />
                <span>LinkedIn</span>
              </a>
            )}

            {sk.twitterHandle && (
              <a href={`https://twitter.com/${sk.twitterHandle}`} target="_blank" rel="noreferrer" className="contact-detail__info-row">
                <TwitterLogo size={14} />
                <span>@{sk.twitterHandle}</span>
              </a>
            )}

            {sk.githubHandle && (
              <a href={`https://github.com/${sk.githubHandle}`} target="_blank" rel="noreferrer" className="contact-detail__info-row">
                <GithubLogo size={14} />
                <span>{sk.githubHandle}</span>
              </a>
            )}

          </div>
        </div>

        {/* Last interaction */}
        {sk.lastInteraction && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Last Interaction</h3>
            <div className="contact-detail__interaction">
              <span className="contact-detail__interaction-when">
                {formatRelativeTime(sk.lastInteraction.at)} via {sk.lastInteraction.channel}
              </span>
              {sk.lastInteraction.snippet && (
                <p className="contact-detail__interaction-snippet">{sk.lastInteraction.snippet}</p>
              )}
            </div>
          </div>
        )}

        {/* Next reach-out */}
        {sk.nextReachOutAt && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Suggested Reach-Out</h3>
            <div className="contact-detail__reachout">
              <ArrowRight size={13} />
              <span>{formatDate(sk.nextReachOutAt)}</span>
              {sk.nextReachOutReason && (
                <span className="contact-detail__reachout-reason"> — {sk.nextReachOutReason}</span>
              )}
            </div>
          </div>
        )}

        {/* Topics */}
        {sk.topics && sk.topics.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Topics We've Discussed</h3>
            <div className="contact-detail__topics">
              {sk.topics.map((t) => (
                <span key={t} className="contact-detail__topic-chip">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Recent signals */}
        {sk.signals && sk.signals.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Recent Signals</h3>
            <div className="contact-detail__signals">
              {sk.signals.slice(0, 5).map((signal) => (
                <div key={signal.id} className="contact-detail__signal">
                  <span className="contact-detail__signal-icon">
                    <SignalIcon type={signal.type} />
                  </span>
                  <div className="contact-detail__signal-content">
                    <span className="contact-detail__signal-title">{signal.title}</span>
                    {signal.body && <p className="contact-detail__signal-body">{signal.body}</p>}
                    <span className="contact-detail__signal-date">
                      {formatRelativeTime(signal.detectedAt)} · {signal.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {sk.tags && sk.tags.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Tags</h3>
            <div className="contact-detail__topics">
              {sk.tags.map((t) => (
                <span key={t} className="contact-detail__tag-chip">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {sk.notes && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Notes</h3>
            <p className="contact-detail__notes">{sk.notes}</p>
          </div>
        )}

        {/* Sources */}
        {sk.sources && sk.sources.length > 0 && (
          <div className="contact-detail__sources">
            <div className="contact-detail__sources-row">
              {sk.sources.map((s) => <SourceBadge key={s} source={s} />)}
            </div>
            {sk.lastEnrichedAt && (
              <span className="contact-detail__sources-enriched">
                Last enriched {formatRelativeTime(sk.lastEnrichedAt)}
              </span>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
